/**
 * NewMeetingDialog — create a meeting from DeliverPro, mapped to the project and to the
 * meeting-intelligence workflow (Task #9, Tier A — no Microsoft Graph), or log one that was held
 * outside DeliverPro (T-17).
 *
 * Two modes, chosen with a native radio group at the top of the form:
 *
 * "Schedule a new meeting" (Task #9, unchanged):
 *   1. The user names the meeting and picks its CASDM phase + scope (and optionally a date).
 *   2. POST /api/projects/{projectId}/meetings creates a `project_meetings` row. The SERVER builds
 *      the canonical Avoma `name_template`, which is what lets the Avoma sync attach the recording
 *      → transcript → CIA analysis to this project.
 *   3. The dialog switches to a success view offering "Create in Teams" — the existing
 *      `buildTeamsDeepLink()` with the new row's `name_template` as the subject, so the Teams
 *      meeting title is byte-identical to what the matcher looks for. The link is OFFERED rather
 *      than auto-opened: a `window.open` after an async request is blocked by most popup blockers.
 *
 * "Log a meeting that already happened" (T-17): same name/phase/scope plus a REQUIRED https link and
 * an optional date held. One POST — the server classifies the link (Avoma → transcript ingest,
 * anything else → external evidence link). No Teams step: success toasts and closes the dialog.
 *
 * Validation mirrors packages/meetings/validation.ts `CreateMeetingSchema` (React Hook Form + Zod).
 * Accessibility: DS `Dialog` (Radix — focus trap, Esc closes), labelled controls, the mode and scope
 * choices are native radio groups inside `<fieldset>`/`<legend>`, and errors are announced via the
 * DS Input/Select/DatePicker error slots.
 */
import { forwardRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { isAxiosError } from "axios";
import { ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  useCreateProjectMeeting,
  type CreatedProjectMeeting,
  type CreateProjectMeetingInput,
} from "@/hooks/useMeetings";
import { buildTeamsDeepLink } from "@/lib/teams-deep-link";
import { phaseLabel } from "@/lib/phases";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TeamsIcon } from "@/components/icons/TeamsIcon";
import { newMeetingMessages as msg } from "./new-meeting-messages";

const PHASES = ["Phase 0", "Phase 1", "Phase 2", "Phase 3", "Phase 4"] as const;
const MODES = ["schedule", "log"] as const;
export type NewMeetingMode = (typeof MODES)[number];

/** Mirrors MEETING_NAME_MIN/MAX and MEETING_LINK_MAX in packages/meetings/validation.ts. */
export const MEETING_NAME_MIN = 3;
export const MEETING_NAME_MAX = 120;
export const MEETING_LINK_MAX = 2000;

const LOCAL_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/;
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Mode-dependent rules live in `superRefine` so a value left in a field that the OTHER mode hides
 * (e.g. a half-filled schedule date) can never block submission of the visible form.
 * `mode`/`link`/`logged_date` are optional so a schedule-only body (pre-T-17 shape) still parses.
 */
export const newMeetingFormSchema = z
  .object({
    mode: z.enum(MODES).optional(),
    meeting_name: z
      .string()
      .trim()
      .min(MEETING_NAME_MIN, msg.errors.nameTooShort)
      .max(MEETING_NAME_MAX, msg.errors.nameTooLong)
      .refine((v) => !v.includes("|"), { message: msg.errors.namePipe }),
    phase: z.enum(PHASES, { message: msg.errors.phaseRequired }),
    scope: z.enum(["internal", "customer"]),
    /**
     * Schedule mode. Local date-time in `<input type="datetime-local">` format ("2026-10-08T15:00")
     * — '' when not set. Composed by `MeetingDateTimeField` from a DatePicker + time input; a
     * half-filled pair is held as "2026-10-08T" / "T15:00" so it can be rejected instead of dropped.
     */
    meeting_date: z.string(),
    /** Log mode. The meeting URL (Avoma recording or Teams/Zoom/Meet/… invite). */
    link: z.string().trim().optional(),
    /** Log mode. 'YYYY-MM-DD' or ''. */
    logged_date: z.string().optional(),
  })
  .superRefine((v, ctx) => {
    if ((v.mode ?? "schedule") === "schedule") {
      if (v.meeting_date !== "" && !LOCAL_DATETIME_RE.test(v.meeting_date)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["meeting_date"],
          message: msg.errors.dateTimeIncomplete,
        });
      }
      return;
    }
    const link = v.link ?? "";
    if (link === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["link"],
        message: msg.errors.linkRequired,
      });
    } else if (link.length > MEETING_LINK_MAX) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["link"],
        message: msg.errors.linkTooLong,
      });
    } else if (!isHttpsUrl(link)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["link"],
        message: msg.errors.linkHttps,
      });
    }
    const day = v.logged_date ?? "";
    if (day !== "" && !DATE_ONLY_RE.test(day)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["logged_date"],
        message: msg.errors.dateTimeIncomplete,
      });
    }
  });

export type NewMeetingFormValues = z.infer<typeof newMeetingFormSchema>;

const DEFAULTS: NewMeetingFormValues = {
  mode: "schedule",
  meeting_name: "",
  // Placeholder until the user picks one — fails the enum, so the select shows its error.
  phase: "" as NewMeetingFormValues["phase"],
  scope: "internal",
  meeting_date: "",
  link: "",
  logged_date: "",
};

/** datetime-local ("2026-10-08T15:00", local time) → ISO-8601 with offset, or undefined. */
function toIsoDate(local: string): string | undefined {
  if (!local) return undefined;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/** Today as local 'YYYY-MM-DD' — the latest selectable "date held" for a past meeting. */
function todayLocal(): string {
  const d = new Date();
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Build the POST body for the active mode from validated form values. */
export function toCreatePayload(
  values: NewMeetingFormValues,
): CreateProjectMeetingInput {
  const base = {
    meeting_name: values.meeting_name,
    phase: values.phase,
    scope: values.scope,
  };
  if ((values.mode ?? "schedule") === "log") {
    return {
      ...base,
      meeting_date: values.logged_date ? values.logged_date : undefined,
      link: values.link,
    };
  }
  return { ...base, meeting_date: toIsoDate(values.meeting_date) };
}

/**
 * Date + time pair replacing the former `<input type="datetime-local">`. Emits the same local
 * "YYYY-MM-DDTHH:mm" string ('' when both halves are empty), so `toIsoDate` and the POST payload
 * are unchanged. The visible label names the date picker (so `htmlFor` still resolves) and the
 * time input carries its own accessible name ("Time"). No extra `role="group"`: naming it with
 * the same label would announce "Date and time" twice.
 */
const MeetingDateTimeField = forwardRef<
  HTMLButtonElement,
  {
    value: string;
    onChange: (value: string) => void;
    onBlur: () => void;
    error?: string;
  }
>(({ value, onChange, onBlur, error }, ref) => {
  const [datePart = "", timePart = ""] = value ? value.split("T") : [];
  const compose = (date: string, time: string): void =>
    onChange(date || time ? `${date}T${time}` : "");
  const errorId = "new-meeting-date-error";

  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor="new-meeting-date"
        className="text-sm font-medium text-text-primary"
      >
        {msg.fields.date}
      </label>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <DatePicker
            ref={ref}
            id="new-meeting-date"
            value={datePart}
            onChange={(date) => compose(date, timePart)}
            onBlur={onBlur}
            placeholder={msg.placeholders.date}
            error={error}
          />
        </div>
        <div className="w-32 shrink-0">
          <Input
            id="new-meeting-time"
            type="time"
            aria-label={msg.fields.time}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            className={error ? "border-error" : undefined}
            value={timePart}
            onChange={(e) => compose(datePart, e.target.value)}
            onBlur={onBlur}
          />
        </div>
      </div>
    </div>
  );
});
MeetingDateTimeField.displayName = "MeetingDateTimeField";

function errorMessage(err: unknown): string {
  if (isAxiosError<{ message?: string }>(err)) {
    return err.response?.data?.message ?? msg.status.createFailure;
  }
  return msg.status.createFailure;
}

export interface NewMeetingDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NewMeetingDialog({
  projectId,
  open,
  onOpenChange,
}: NewMeetingDialogProps): JSX.Element {
  const create = useCreateProjectMeeting(projectId);
  const [created, setCreated] = useState<CreatedProjectMeeting | null>(null);

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
    watch,
  } = useForm<NewMeetingFormValues>({
    resolver: zodResolver(newMeetingFormSchema),
    defaultValues: DEFAULTS,
  });
  const mode: NewMeetingMode = watch("mode") ?? "schedule";
  const isLog = mode === "log";

  const close = (next: boolean): void => {
    onOpenChange(next);
    if (!next) {
      // Reset after close so reopening starts clean.
      setCreated(null);
      reset(DEFAULTS);
    }
  };

  const onSubmit = (values: NewMeetingFormValues): void => {
    const logging = (values.mode ?? "schedule") === "log";
    create.mutate(toCreatePayload(values), {
      onSuccess: (row) => {
        if (logging) {
          // Nothing left to do in Teams for a meeting that already happened.
          toast.success(msg.status.logged);
          close(false);
          return;
        }
        setCreated(row);
        toast.success(msg.status.created);
      },
      onError: (err) => {
        toast.error(errorMessage(err));
      },
    });
  };

  const teamsLink = created
    ? buildTeamsDeepLink({
        name_template: created.name_template,
        meeting_name: created.meeting_name,
      })
    : null;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{created ? msg.success.title : msg.title}</DialogTitle>
          <DialogDescription>
            {created
              ? msg.success.description
              : isLog
                ? msg.logDescription
                : msg.description}
          </DialogDescription>
        </DialogHeader>

        {created ? (
          <div className="space-y-4">
            <div className="rounded border border-outlines bg-subtle-bg px-3 py-2">
              <p className="text-xs text-text-secondary">
                {msg.success.subjectLabel}
              </p>
              <p
                className="break-words text-sm font-medium text-text-primary"
                data-testid="new-meeting-subject"
              >
                {teamsLink?.subject ?? created.name_template}
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => close(false)}
              >
                {msg.actions.done}
              </Button>
              {teamsLink && (
                <Button asChild>
                  <a
                    href={teamsLink.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${msg.actions.openTeams} — ${teamsLink.label} (${msg.opensInNewTab})`}
                  >
                    <TeamsIcon className="h-4 w-4" />
                    {msg.actions.openTeams}
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                  </a>
                </Button>
              )}
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-4"
            noValidate
          >
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium text-text-primary">
                {msg.mode.legend}
              </legend>
              <div className="flex flex-col gap-2">
                {MODES.map((m) => (
                  <label
                    key={m}
                    className="flex items-center gap-2 text-sm text-text-primary"
                  >
                    <input
                      type="radio"
                      value={m}
                      className="h-4 w-4 accent-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                      {...register("mode")}
                    />
                    {msg.mode[m]}
                  </label>
                ))}
              </div>
            </fieldset>

            <Input
              id="new-meeting-name"
              label={msg.fields.name}
              required
              aria-required="true"
              maxLength={MEETING_NAME_MAX}
              placeholder={msg.placeholders.name}
              error={errors.meeting_name?.message}
              {...register("meeting_name")}
            />

            <Controller
              control={control}
              name="phase"
              render={({ field }) => (
                <Select
                  id="new-meeting-phase"
                  label={msg.fields.phase}
                  required
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={msg.placeholders.phase}
                  error={errors.phase?.message}
                  options={PHASES.map((p) => ({
                    value: p,
                    label: phaseLabel(p),
                  }))}
                />
              )}
            />

            <fieldset className="space-y-2">
              <legend className="text-sm font-medium text-text-primary">
                {msg.fields.scope}
              </legend>
              <div className="flex gap-6">
                {(["internal", "customer"] as const).map((s) => (
                  <label
                    key={s}
                    className="flex items-center gap-2 text-sm text-text-primary"
                  >
                    <input
                      type="radio"
                      value={s}
                      className="h-4 w-4 accent-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                      {...register("scope")}
                    />
                    {msg.scope[s]}
                  </label>
                ))}
              </div>
            </fieldset>

            {isLog ? (
              <>
                <Input
                  id="new-meeting-link"
                  type="url"
                  inputMode="url"
                  autoComplete="off"
                  label={msg.fields.link}
                  required
                  aria-required="true"
                  maxLength={MEETING_LINK_MAX}
                  placeholder={msg.placeholders.link}
                  error={errors.link?.message}
                  {...register("link")}
                />
                <Controller
                  control={control}
                  name="logged_date"
                  render={({ field }) => (
                    <div className="flex flex-col gap-1.5">
                      <label
                        htmlFor="new-meeting-logged-date"
                        className="text-sm font-medium text-text-primary"
                      >
                        {msg.fields.loggedDate}
                      </label>
                      <DatePicker
                        ref={field.ref}
                        id="new-meeting-logged-date"
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholder={msg.placeholders.date}
                        max={todayLocal()}
                        error={errors.logged_date?.message}
                      />
                    </div>
                  )}
                />
                <p className="text-xs text-text-secondary">{msg.linkHint}</p>
              </>
            ) : (
              <>
                <Controller
                  control={control}
                  name="meeting_date"
                  render={({ field }) => (
                    <MeetingDateTimeField
                      ref={field.ref}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={errors.meeting_date?.message}
                    />
                  )}
                />
                <p className="text-xs text-text-secondary">
                  {msg.matchingHint}
                </p>
              </>
            )}

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => close(false)}
              >
                {msg.actions.cancel}
              </Button>
              <Button
                type="submit"
                disabled={create.isPending}
                aria-busy={create.isPending}
              >
                {create.isPending && (
                  <Loader2
                    className="h-4 w-4 animate-spin"
                    aria-hidden="true"
                  />
                )}
                {isLog ? msg.actions.log : msg.actions.create}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default NewMeetingDialog;
